/**
 * The tenth state — a screen that threw while rendering.
 *
 * The other nine states describe data. This one describes the build: a `TypeError`
 * in one panel's render used to take the whole document with it (found when
 * `/time-tracking` crashed and left a white page — no rail, no top bar, no way back
 * that was not the browser's reload button). React unmounts the entire tree on an
 * uncaught render error, so the only way to keep the frame is to catch below it.
 *
 * Hence: mounted inside `AppShell`, around `{children}` only. The rail and the top
 * bar are outside the boundary and stay usable, so the next screen is one click
 * away rather than one reload away.
 *
 * ── What reaches the reader, and what does not ───────────────────────────────
 *
 * The stack goes to `console.error` and nowhere else. A stack on the paper names
 * internal modules and minified symbols to someone who cannot act on either, and on
 * a shared screen it is a disclosure. What the paper carries instead is a short
 * trace id, generated per catch, of the same shape `ErrorCodeLine` prints for a
 * refusal — one string a person can read aloud to an administrator who *can* go and
 * find the stack.
 *
 * Class component because `componentDidCatch`/`getDerivedStateFromError` have no
 * hook equivalent; the note itself is a function component so it can use `useT`.
 */

import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react';

import { useT } from '../i18n';
import { ErrorCodeLine } from './ErrorCodeLine';
import { RetryAction } from './RetryAction';
import { StatusNote } from './StatusNote';

/** The enum this state reports as. Never rendered visibly — see `ErrorCodeLine`. */
const RENDER_ERROR_CODE = 'WORKFORCE_UI_RENDER_FAILED';

/** Eight base-36 characters: short enough to say on the phone, wide enough not to collide in a day. */
function traceId(): string {
  return Math.random().toString(36).slice(2, 10).toUpperCase();
}

function RenderErrorNote({
  trace,
  onRetry,
}: {
  readonly trace: string;
  readonly onRetry: () => void;
}): ReactNode {
  const t = useT();
  return (
    <StatusNote
      severity="critical"
      // It appeared because of what the person just did — navigating here — so it
      // interrupts rather than waits its turn.
      role="alert"
      title={t('renderError.title')}
      actions={<RetryAction onRetry={onRetry} testId="render-error-retry" />}
      testId="render-error"
    >
      <p>{t('renderError.body')}</p>
      <ErrorCodeLine code={RENDER_ERROR_CODE} correlationId={trace} />
    </StatusNote>
  );
}

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly trace: string | null;
  /** Bumped on retry so the recovered subtree mounts fresh rather than re-using stale state. */
  readonly attempt: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { trace: null, attempt: 0 };

  static getDerivedStateFromError(): Pick<ErrorBoundaryState, 'trace'> {
    return { trace: traceId() };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- the only channel that may carry a stack.
    console.error(`[workforce-pulse] ${RENDER_ERROR_CODE} ${this.state.trace ?? ''}`, error, info);
  }

  private readonly retry = (): void => {
    this.setState((prev) => ({ trace: null, attempt: prev.attempt + 1 }));
  };

  override render(): ReactNode {
    const { trace, attempt } = this.state;
    if (trace !== null) return <RenderErrorNote trace={trace} onRetry={this.retry} />;
    return <Fragment key={attempt}>{this.props.children}</Fragment>;
  }
}
